import React from 'react';
import { ScrollView } from 'react-native';

import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import {
  useLinkTo,
  useCanRender,
  useResponsiveEffect,
  useGetUtmParams,
  useInterval,
} from '../../../../hooks';

import analytics from '../../../../modules/analytics';
import HeaderServiceButton from './HeaderServiceButton';
import { toSlugCase } from '../../../../modules/stringUtils';
import { SearchPopoverModal } from '../../../consumer/search/SearchPopover';
import useProviderRecommendations from '../hooks/useProviderRecommendations';

import { ROTATING_HEADER_TEXTS } from './constants';
import AnimatedHeaderText from './AnimatedHeaderText';
import './HeaderServices.module.scss';
import { SEARCH_ROUTE } from '../../../../route-names';

type HeaderServicesProps = {
  inCOM705Test?: boolean;
  providerId: string | number;
};

function HeaderServices({
  inCOM705Test = false,
  providerId,
}: HeaderServicesProps) {
  const linkTo = useLinkTo();
  const is = useResponsiveEffect();
  const recommendations = useProviderRecommendations(providerId);
  const [searchModalOpen, setSearchModalOpen] = React.useState<boolean>(false);
  const utmParams = useGetUtmParams();

  const [rotatingHeaderTextIndex, setRotatingHeaderTextIndex] = React.useState<number>(0);
  useInterval(() => {
    setRotatingHeaderTextIndex(curr => {
      if (curr === ROTATING_HEADER_TEXTS.length - 1) {
        return 0;
      }

      return curr + 1;
    });
  }, 6000);

  const handleCategoryClick = (category: string) => {
    analytics.track('client_proprofile_header_service_clicked', {
      category,
    });
    linkTo(SEARCH_ROUTE, {
      q: toSlugCase(category),
      loc: `${toSlugCase(recommendations.city)}-${toSlugCase(recommendations.state)}`,
      ...utmParams,
    });
  };

  const handleMoreClick = () => {
    analytics.track('client_proprofile_header_more_clicked');
    setSearchModalOpen(true);
  };

  const handleSearchClose = React.useCallback(() => {
    setSearchModalOpen(false);
  }, []);

  const showComponent = useCanRender(
    recommendations?.found
    && !!recommendations?.city
    && !!recommendations?.state,
  );

  return showComponent && (
    <Box
      minHeight={64}
      minWidth="100%"
      color="white"
      styleName="sticky-service-tiles"
    >
      {
        inCOM705Test && (
          <Box
            alignItems={is.desktop ? 'center' : 'flex-start'}
            overflow="hidden"
            minHeight={36}
            testID="header_service_tiles_animated__header_text_container"
          >
            {
              ROTATING_HEADER_TEXTS.map((headerText, idx) => (
                <AnimatedHeaderText
                  headerText={headerText}
                  key={headerText}
                  inView={idx === rotatingHeaderTextIndex}
                />
              ))
            }
          </Box>
        )
      }
      <Box
        justifyContent={is.desktop ? 'center' : 'flex-start'}
        borderStyle="solid"
        borderBottomColor={BrandColor.LightGrey}
        borderBottomWidth={1}
        testID="profile-header-services-tiles"
      >
        <Inline
          space={4}
          paddingX={16}
          alignItems="center"
        >
          <ScrollView horizontal>
            <Inline
              space={is.desktop ? 10 : 8}
              paddingTop={inCOM705Test ? 0 : 12}
              paddingBottom={inCOM705Test ? 12 : 8}
            >
              {recommendations.top_categories.map(eachTopCatergory => (
                <HeaderServiceButton
                  category={eachTopCatergory.category}
                  key={eachTopCatergory.category}
                  onCategoryClick={handleCategoryClick}
                />
              ))}
              <HeaderServiceButton
                category="More"
                onCategoryClick={handleMoreClick}
              />
            </Inline>
          </ScrollView>
        </Inline>
      </Box>
      {searchModalOpen && (
        <SearchPopoverModal
          onClose={handleSearchClose}
        />
      )}
    </Box>
  );
}

export default React.memo(HeaderServices);
